import debounce from 'lodash-es/debounce'
import times from 'lodash-es/times'
import { PureComponent, createRef } from 'react'
import { withResizeDetector } from 'react-resize-detector'
import 'intersection-observer'
import { polyfill } from 'smoothscroll-polyfill'
import SvgArrow from '#/assets/images/carousel/arrow-forward.svg'
import cn from '#/utils/classnames'
import styles from './styles.module.scss'
import type { Props, State } from './types'

if (typeof window !== 'undefined') {
  polyfill()
}

class Carousel extends PureComponent<Props, State> {
  static defaultProps: Partial<Props> = {
    autoplay: false,
    autoplaySpeed: 5000,
    isMobile: false,
    slidesToScroll: 1,
    slidesToShow: 1
  }

  refContainer: React.RefObject<HTMLDivElement>
  timer?: NodeJS.Timeout
  hammer?: HammerManager

  constructor(props: Props) {
    super(props)
    this.refContainer = createRef()

    this.state = { currentIndex: 0 }
  }

  async componentDidMount() {
    const { autoplay, autoplaySpeed, isMobile } = this.props
    if (autoplay) {
      this.timer = setTimeout(this.onAutoplay, autoplaySpeed)
    }

    if (!isMobile && this.refContainer.current) {
      const { default: Hammer } = await import('hammerjs')
      const hammer = new Hammer(this.refContainer.current)
      hammer.on('swipe', this.handleSwipe)
      this.hammer = hammer
    }
  }

  componentDidUpdate({ width }: Props) {
    if (width !== this.props.width) {
      this.handleScroll()
    }
  }

  componentWillUnmount() {
    if (this.timer) clearTimeout(this.timer)
    this.handleScrollEnd.cancel()

    if (this.hammer) {
      this.hammer.stop(true)
      this.hammer.destroy()
    }
  }

  handleScrollEnd = debounce(() => {
    const { autoplay, autoplaySpeed } = this.props
    if (autoplay) {
      this.timer = setTimeout(this.onAutoplay, autoplaySpeed)
    }
  }, 100)

  private get activeDot() {
    const { currentIndex } = this.state
    return Math.ceil(currentIndex / this.slidesToScroll)
  }

  private get dotsCount() {
    return Math.ceil(
      (this.props.children.length - this.slidesToShow + 1) / this.slidesToScroll
    )
  }

  private get settings() {
    const { responsive } = this.props
    if (responsive) {
      return responsive.find(e => e.breakpoint > this.props.width)
    }

    return undefined
  }

  private get slidesToScroll() {
    if (this.settings && this.settings.slidesToScroll) {
      return this.settings.slidesToScroll
    } else {
      return this.props.slidesToScroll || 1
    }
  }

  private get slidesToShow() {
    if (this.settings && this.settings.slidesToShow) {
      return this.settings.slidesToShow
    } else {
      return this.props.slidesToShow || 1
    }
  }

  private get slideWidth() {
    if (!this.refContainer.current) return 1
    return this.refContainer.current.clientWidth / this.slidesToShow
  }

  moveTo = (index: number, behavior: ScrollOptions['behavior'] = 'smooth') => {
    if (!this.refContainer.current) return

    this.refContainer.current.scrollTo({
      left: index * this.slideWidth,
      behavior
    })
  }

  onPrev = (ev?: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (ev) {
      ev.preventDefault()
      ev.stopPropagation()
    }

    const { currentIndex } = this.state

    this.moveTo(Math.max(currentIndex - this.slidesToScroll, 0))
  }

  onNext = (ev?: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (ev) {
      ev.preventDefault()
      ev.stopPropagation()
    }

    const { children } = this.props
    const { currentIndex } = this.state

    this.moveTo(
      Math.min(currentIndex + this.slidesToScroll, children.length - 1)
    )
  }

  onAutoplay = () => {
    const { children } = this.props
    const { currentIndex } = this.state
    if (currentIndex >= children.length - this.slidesToShow) {
      this.moveTo(0, 'auto')
    } else {
      this.onNext()
    }
  }

  handleScroll = () => {
    if (!this.refContainer.current) return

    const index = Math.round(
      this.refContainer.current.scrollLeft / this.slideWidth
    )

    this.setState({ currentIndex: index })

    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = undefined
    }
    this.handleScrollEnd()
  }

  handleSwipe = (ev: HammerInput) => {
    const ele = this.refContainer.current as HTMLDivElement

    ele.scrollTo({
      left: ele.scrollLeft - ev.velocityX * Math.abs(ev.deltaX),
      behavior: 'smooth'
    })
  }

  render() {
    const {
      children,
      classes
    } = this.props
    const { currentIndex } = this.state

    return (
      <div className={cn(styles['th-carousel-container'], classes?.root)}>
        <div className={classes?.container}>
          <div
            ref={this.refContainer}
            className={cn(styles['th-carousel'], classes?.carousel)}
            onScroll={this.handleScroll}
          >
            {children.map((e, index) => (
              <div
                key={index}
                className={cn(styles['th-slide'], { [styles['th-active']]: index === currentIndex })}
                style={{ width: `${100 / this.slidesToShow}%` }}
              >
                {e}
              </div>
            ))}
          </div>
        </div>

        <div className={styles['th-carousel-actions']}>
          <div
            className={cn(
              styles['th-action-left'],
              { [styles['th-disabled']]: currentIndex <= 0 },
              classes?.prevButton
            )}
            onClick={this.onPrev}
          >
            <SvgArrow />
          </div>
          <div className={styles['th-dots']}>
            {times(this.dotsCount, index => (
              <div
                key={index}
                className={cn(styles['th-dot'], { [styles['th-active']]: this.activeDot === index })}
                onClick={() =>
                  this.moveTo(index * this.slidesToScroll, 'smooth')}
              />
            ))}
          </div>
          <div
            className={cn(
              styles['th-action-right'],
              {
                [styles['th-disabled']]:
                  currentIndex >= children.length - this.slidesToShow
              },
              classes?.nextButton
            )}
            onClick={this.onNext}
          >
            <SvgArrow />
          </div>
        </div>
      </div>
    )
  }
}

export default withResizeDetector(Carousel, { handleHeight: false, refreshMode: 'debounce', refreshRate: 0 })
